/**
 * Story unlock sequence for the shift slice.
 *
 * WHICH story grants WHAT is authored per level (`unlocksOnComplete` in its
 * level.json), not branched on here. It used to read
 * `if (packId === "scenario-vet-day") return ["cast"]`, with a comment calling
 * Vet Day "story 1" — which stopped being true when the levels were reordered
 * fun-first, leaving the grant on the LAST story where it could never be used.
 * Basic Cut/Frame/Text/Sound remain available throughout.
 */
import type { PackId } from "../content/registry.js";
import { SHIFT_PACK_ORDER, unlocksForPackId } from "../content/registry.js";
import {
  FOLLOWERS_FOR_STORY,
  STARTING_UNLOCKS,
  type UnlockId,
} from "./types.js";

export function unlocksAfterCompleting(packId: PackId): UnlockId[] {
  return unlocksForPackId(packId) as UnlockId[];
}

export function mergeUnlocks(current: UnlockId[], added: UnlockId[]): UnlockId[] {
  const set = new Set<UnlockId>(current);
  for (const u of added) set.add(u);
  return [...STARTING_UNLOCKS.filter((u) => set.has(u)), ...[...set].filter((u) => !STARTING_UNLOCKS.includes(u))];
}

export function hasUnlock(unlocks: readonly UnlockId[], id: UnlockId): boolean {
  return unlocks.includes(id);
}

/** Next story index, or null if shift complete. */
export function nextStoryIndex(completed: readonly PackId[]): number | null {
  for (let i = 0; i < SHIFT_PACK_ORDER.length; i++) {
    if (!completed.includes(SHIFT_PACK_ORDER[i]!)) return i;
  }
  return null;
}

export function canStartStory(
  storyIndex: number,
  followers: number,
  completed: readonly PackId[],
): { ok: boolean; reason?: string } {
  if (storyIndex < 0 || storyIndex >= SHIFT_PACK_ORDER.length) {
    return { ok: false, reason: "No more stories this shift." };
  }
  const packId = SHIFT_PACK_ORDER[storyIndex]!;
  if (completed.includes(packId)) {
    return { ok: false, reason: "Story already landed this shift." };
  }
  // Prior stories must have LANDED >= 1 audience (>= 1 star). `completedStoryIds`
  // is now appended only on a landed publish (Decision D2), so membership here is
  // exactly "prior story has a star". The star-based `storyUnlocked` selector
  // (progress.ts) is the roadmap's equivalent read.
  for (let i = 0; i < storyIndex; i++) {
    if (!completed.includes(SHIFT_PACK_ORDER[i]!)) {
      return { ok: false, reason: "Land the earlier story first." };
    }
  }
  const need = FOLLOWERS_FOR_STORY[storyIndex] ?? 0;
  if (followers < need) {
    return {
      ok: false,
      reason: `Need ${need} followers (have ${followers}). Publish earlier work to grow.`,
    };
  }
  return { ok: true };
}

export function isCastingUnlocked(unlocks: readonly UnlockId[]): boolean {
  return hasUnlock(unlocks, "cast");
}

export function isCaptionDeepUnlocked(unlocks: readonly UnlockId[]): boolean {
  return hasUnlock(unlocks, "captionDeep");
}

export function isSoundDeepUnlocked(unlocks: readonly UnlockId[]): boolean {
  return hasUnlock(unlocks, "soundDeep");
}
